class MapHelper {
  static twoDimMapToRecordOfArray<ItemT>(
    mapOfMap: Map<string | number, Map<string | number, ItemT>>,
  ): Record<string, ItemT[]> {
    const newObj: Record<string, ItemT[]> = {};
    for (const [k, v] of mapOfMap) {
      newObj[k] = Array.from(v.values());
    }
    // newObj[1] = [];
    return newObj;
  }

  // object 속의 key => array 를 이중 map으로 바꾸어준다.
  static recordOfArrayToMap<
    ParentKeyT extends number | string,
    ChildKeyT extends number | string,
    ItemT,
  >(
    obj: Record<ParentKeyT, ItemT[]>,
    mapKeyField: keyof ItemT,
    parentKeyType: 'int' | 'string' = 'int',
  ) {
    const newMap = Object.keys(obj).reduce((acc, key) => {
      const map: Map<ChildKeyT, ItemT> = new Map();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      obj[key as ParentKeyT].forEach((item: any) => {
        map.set(item[mapKeyField], item);
      });
      if (parentKeyType === 'int') {
        acc.set(parseInt(key) as ParentKeyT, map); // object의 key는 항상 string type
      } else {
        acc.set(key as ParentKeyT, map);
      }
      return acc;
    }, new Map<ParentKeyT, Map<ChildKeyT, ItemT>>());

    return newMap as Map<ParentKeyT, Map<ChildKeyT, ItemT>>;
  }

  // obj 속의 obj를 map으로 바꾸어준다.
  static recordOfObjToMap<KeyT extends number | string, ItemT>(
    twoDimObj: Record<KeyT, ItemT>,
    parentKeyType: 'int' | 'string' = 'int',
  ) {
    const newMap = new Map<KeyT, ItemT>();

    Object.entries(twoDimObj).forEach((entry) => {
      if (parentKeyType === 'int') {
        newMap.set(parseInt(entry[0]) as KeyT, entry[1] as ItemT);
      } else {
        newMap.set(entry[0] as KeyT, entry[1] as ItemT);
      }
    });

    return newMap;
  }

  static toArray<V>(map?: Map<unknown, V>): V[] {
    if (!map) return [] as V[];
    return Array.from(map.values()) as V[];
  }

  static toValueNameArray<K, V>(map: Map<K, V>) {
    return Array.from(map, ([value, name]) => ({
      value: value,
      name: name,
    }));
  }

  // 1차원 array를 log n 만에 검색 가능한 map으로 바꾸어준다.
  // obj 속의 obj를 map으로 바꾸어준다.
  static arrayToMap<ItemT extends Record<string, unknown>, KeyT extends number | string = number>(
    arr: ItemT[],
    keyName = 'id',
  ) {
    const newMap = new Map<KeyT, ItemT>();

    arr.forEach((item) => {
      newMap.set(item[keyName] as KeyT, item);
    });

    return newMap;
  }

  static arrayToMapWithKeySelector<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ItemT extends Record<string, any>,
    KeyT extends number | string = number,
  >(arr: ItemT[], keySelector: (item: ItemT) => KeyT) {
    const newMap = new Map<KeyT, ItemT>();

    arr.forEach((item) => {
      newMap.set(keySelector(item), item);
    });

    return newMap;
  }

  static ensureAndGet<K, V>(map: Map<K, V>, key: K, defaultValue: V | (() => V)): V {
    if (!map.has(key)) {
      const valueToSet =
        typeof defaultValue === 'function' ? (defaultValue as () => V)() : defaultValue;
      map.set(key, valueToSet);
    }
    return map.get(key)!; // The non-null assertion is safe here because we know the key exists
  }

  static ensureAndPushToArray<K, V>(map: Map<K, V[]>, key: K, item: V): void {
    const array = MapHelper.ensureAndGet(map, key, []);
    array.push(item);
  }

  static replaceKeyPreservingOrder<K, V>(
    oldMap: Map<K, V>,
    oldKey: K,
    newKey: K,
    newValue: V,
  ): Map<K, V> {
    const newMap = new Map<K, V>();

    for (const [k, v] of oldMap) {
      if (Object.is(k, oldKey)) {
        newMap.set(newKey, newValue);
      } else {
        newMap.set(k, v);
      }
    }
    return newMap;
  }
}

export default MapHelper;
